import * as React from "react";
import { Link } from "@/components/common/Link";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const cookiesData = [{
  category: "Quality Management",
  examples: "ISO 9001:2015",
  purpose: "Establishes a robust framework for consistent project delivery, ensuring all engineering and construction outputs meet client specifications and international quality benchmarks.",
  duration: "3 Years"
}, {
  category: "Environmental Systems",
  examples: "ISO 14001:2015",
  purpose: "Governs our environmental impact through systematic management of resource consumption, waste reduction, and proactive mitigation of ecological risks across all project sites.",
  duration: "3 Years"
}, {
  category: "Health & Safety",
  examples: "ISO 45001:2018",
  purpose: "The global standard for occupational health and safety, providing a rigorous structure to prevent work-related injury and ill health while fostering a proactive safety culture.",
  duration: "3 Years"
}];
export default function CookiePolicy() {
  return <section data-section-id="226071" id="bottom-up-projects-pty-ltd-iso-certification" className="bg-background py-[120px] text-foreground transition-all duration-700 ease-in-out">
      <div className="container mx-auto px-4 max-w-4xl">
        
        <div className="text-center mb-12 border-b border-primary/20 pb-8">
          <h1 className="font-default text-4xl md:text-6xl font-extrabold text-foreground tracking-tighter mb-4">
            ISO CERTIFICATION APPLICATION
          </h1>
          <p className="text-muted-foreground font-mono text-sm uppercase tracking-widest">
            Certification Status: APPLICATION SUBMITTED - PENDING CERTIFICATION
          </p>
          <p className="mt-6 text-foreground/80 leading-relaxed max-w-2xl mx-auto font-default text-lg">
            Bottom Up Projects Pty Ltd has applied for ISO certifications and is currently undergoing the assessment and audit process to align our engineering excellence with the highest global standards for safety and sustainability.
          </p>
        </div>

        <div className="space-y-12">
          
          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-4">
              1. What is ISO Certification?
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4 font-default">
              ISO certifications are internationally recognized standards that validate an organisation's ability to maintain high-quality operations. For Bottom Up Projects, these certifications serve as a technical guarantee of our operational integrity, specifically focusing on:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/80 leading-relaxed font-default">
              <li>Standardization of complex engineering workflows.</li>
              <li>Rigorous hazard identification and risk assessment protocols.</li>
              <li>Continuous performance monitoring and internal auditing.</li>
              <li>Legal and regulatory compliance across diverse project jurisdictions.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-6">
              2. Core Management Systems
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-6 font-default">
              The following technical framework details our current certifications, their specific application within our project lifecycle, and their audit validity periods.
            </p>
            
            <div className="rounded-none border border-primary/20 overflow-hidden shadow-sm">
              <Table>
                <TableHeader className="bg-primary/10">
                  <TableRow className="hover:bg-primary/10 border-b border-primary/20">
                    <TableHead className="text-foreground font-bold py-4 font-mono text-xs uppercase tracking-wider">Standard / Code</TableHead>
                    <TableHead className="text-foreground font-bold py-4 font-mono text-xs uppercase tracking-wider">Technical Scope</TableHead>
                    <TableHead className="text-foreground font-bold py-4 whitespace-nowrap font-mono text-xs uppercase tracking-wider">Validity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-background">
                  {cookiesData.map((cookie, i) => <TableRow key={i} data-index={i} className="border-b border-primary/10 last:border-0">
                      <TableCell className="align-top py-4">
                        <div className="font-default font-bold text-foreground mb-1">{cookie.category}</div>
                        <div className="text-xs text-primary font-mono font-medium">{cookie.examples}</div>
                      </TableCell>
                      <TableCell className="align-top py-4 text-foreground/80 leading-relaxed font-default text-sm">
                        {cookie.purpose}
                      </TableCell>
                      <TableCell className="align-top py-4 text-foreground/80 whitespace-nowrap font-mono text-xs">
                        {cookie.duration}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </section>

          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-4">
              3. Compliance Management
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-default text-xl font-semibold text-foreground mb-2">3.1 Internal Audit Protocol</h3>
                <p className="text-foreground/80 leading-relaxed mb-2 font-default">
                  Our dedicated compliance team performs monthly site inspections and quarterly internal audits to ensure:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-foreground/80 leading-relaxed font-default">
                  <li>Adherence to Safe Work Method Statements (SWMS).</li>
                  <li>Calibration of all technical plant and equipment.</li>
                  <li>Real-time reporting of non-conformance for immediate resolution.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-default text-xl font-semibold text-foreground mb-2">3.2 Regulatory Alignment</h3>
                <p className="text-foreground/80 leading-relaxed mb-2 font-default">
                  Beyond ISO standards, we maintain strict alignment with Australian Standards (AS/NZS) and local building codes. Our documentation is digitized for transparent reporting to clients and government bodies, accessible via:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-foreground/80 leading-relaxed font-default">
                  <li>The Procore Project Management Suite.</li>
                  <li>On-site digital compliance registers.</li>
                  <li>Annual Sustainability Reports.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-4">
              4. Third-Party Auditing
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4 font-default">
              To maintain our certifications, Bottom Up Projects undergoes annual surveillance audits by accredited external bodies. These independent reviews verify that our Integrated Management System remains effective and continues to evolve with industry best practices.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/80 leading-relaxed mb-4 font-default">
              <li>Unannounced site inspections for safety verification.</li>
              <li>Deep-dive financial and operational risk assessments.</li>
              <li>Interviews with project managers and field personnel.</li>
            </ul>
            <p className="text-foreground/80 leading-relaxed font-default">
              This external validation ensures that our clients receive services that are not only high-quality but also ethically and legally sound.
            </p>
          </section>

          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-4">
              5. Certification Lifecycle
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4 font-default">
              We manage our certifications through two distinct technical cycles:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/80 leading-relaxed font-default">
              <li><strong>Continuous Monitoring:</strong> Real-time data collection from project sites used to identify trends and potential areas for system improvement before they impact project delivery.</li>
              <li><strong>Recertification Cycle:</strong> A comprehensive triennial review where our entire IMS is re-evaluated against updated international standards to ensure long-term operational resilience.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-4">
              6. Governance & Ethics
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4 font-default">
              ISO certification is more than a badge; it is a commitment to ethical governance. By utilizing these frameworks, we ensure transparency in our supply chain and accountability in our decision-making processes.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/80 leading-relaxed font-default">
              <li>Zero-tolerance policy for safety non-compliance.</li>
              <li>Whistleblower protection for reporting system failures.</li>
              <li>Mandatory ISO induction for all new contractors and partners.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-default text-2xl font-bold text-foreground mb-4">
              7. Framework Evolution
            </h2>
            <p className="text-foreground/80 leading-relaxed font-default">
              Bottom Up Projects reserves the right to upgrade our compliance frameworks as international standards evolve. We actively participate in industry forums to stay ahead of regulatory changes in the Australian construction and infrastructure sectors.
            </p>
          </section>

        </div>
      </div>
    </section>;
}